// A hand-authored, bounded "board game" map — an alternative to the usual
// infinite procedural terrain, selectable from the lobby (multiplayer) or
// the island picker (solo). An elongated ~5x15-hex main island with two
// markets, a small densely-wooded knoll island to its north and south,
// and three shipwrecks, surrounded by a shore-hugging depth gradient that
// gives every fish habitat (deep/coral/mangrove/shellfish/open) a place
// to occur, all within a compact, walkable area.
//
// Design approach: rather than generating this at runtime, the shape was
// hand-designed and verified offline (habitat coverage, tree density,
// spawn-point viability) against this exact same height function — see
// the design notes in the project's working history for how the seed/
// coordinates were chosen. This module just holds the result.

import { hash2, hexToWorld, setHeightOverride, SEA_LEVEL, type Axial } from '@world/terrain';
import { spawnTree } from '@entities/trees';
import { villages, extraVillages, type Village } from '@economy/market';
import { wrecks, setTopTypeOverride } from '@world/chunks';
import { setHabitatOverride, type Habitat } from '@world/water';
import type { WreckSite } from '@systems/diving';
import { FISH_TYPES } from '@entities/fish';
import { player } from '@entities/player';

// Only feeds weather/rival variety (applyWeatherSeed/applyRivalSeed both
// derive from getTerrainSeed()) — the terrain shape itself is entirely
// the lookup table below, not this seed's noise.
export const FIXED_MAP_SEED = 9001;
export const FIXED_MAP_LABEL = 'The Board';

// 5 rows (r=-2..2) x 15 columns (q=-7..7) = 75 hexes. The perimeter sits
// right at SEA_LEVEL (still dry land — isWaterAt only triggers below it)
// so the shore-to-water step down to the surrounding shallows is a single
// level, not two. The middle 3 rows across most of the length are one
// level up for a real forested interior (tapering back down near the two
// village ends, so those sites keep flat, same-height neighbors for
// buildVillageMesh's rigid geometry). chunks.ts's genColumn textures
// anything at height <= SEA_LEVEL+1 as sand by default — since the
// interior needs to stay just one level up (not two) to keep the beach
// gentle, it's force-textured as grass below instead, via
// setTopTypeOverride, rather than pushed up an extra height level.
const ISLAND_HEIGHTS = new Map<string, number>();
const GRASS_HEXES = new Set<string>();
for (let r = -2; r <= 2; r++) {
  for (let q = -7; q <= 7; q++) ISLAND_HEIGHTS.set(q + ',' + r, SEA_LEVEL);
}
for (let r = -1; r <= 1; r++) {
  for (let q = -4; q <= 4; q++) {
    const k = q + ',' + r;
    ISLAND_HEIGHTS.set(k, SEA_LEVEL + 1);
    GRASS_HEXES.add(k);
  }
}

// Snapshot of just the main island's own hexes (before the knolls below
// are added to ISLAND_HEIGHTS) — used to zone the main island's own
// shallows for habitat purposes, distinct from each knoll's shallows.
const mainIslandHexList: Axial[] = [...ISLAND_HEIGHTS.keys()].map((k) => {
  const [q, r] = k.split(',').map(Number);
  return { q, r };
});

function axialDistance(q1: number, r1: number, q2: number, r2: number): number {
  return (Math.abs(q1 - q2) + Math.abs(q1 + r1 - q2 - r2) + Math.abs(r1 - r2)) / 2;
}

// Two small, densely-wooded knoll islands to the north and south of the
// main island — a radius-2 hex disc each: a thin beach ring around a
// forested middle.
function addKnoll(cq: number, cr: number): void {
  for (let dq = -2; dq <= 2; dq++) {
    for (let dr = -2; dr <= 2; dr++) {
      const d = axialDistance(0, 0, dq, dr);
      if (d > 2) continue;
      const k = (cq + dq) + ',' + (cr + dr);
      if (d <= 1) { ISLAND_HEIGHTS.set(k, SEA_LEVEL + 1); GRASS_HEXES.add(k); }
      else ISLAND_HEIGHTS.set(k, SEA_LEVEL);
    }
  }
}
const NORTH_KNOLL: Axial = { q: 0, r: -12 };
const SOUTH_KNOLL: Axial = { q: 0, r: 12 };
addKnoll(NORTH_KNOLL.q, NORTH_KNOLL.r);
addKnoll(SOUTH_KNOLL.q, SOUTH_KNOLL.r);

// An explicit, narrow (2-hex-wide) gradient between the main island and
// each knoll: shallow (depth 1, wadeable) next to each shore, then deeper
// (depth 2), then a "deep" core (depth 3 — just past isShallowWaterAt's
// wadeable threshold, no need to go all the way to the board's absolute
// deepest tier), then back to shallow at the other shore. Non-wadeable
// only requires depth 2+ (water.ts's isShallowWaterAt/canWade only allow
// crossing at exactly depth 1), so the depth-2-and-3 rows are what
// actually block wading straight across; the shallow rows right at each
// shore are still wadeable, same as any normal beach.
const GRADIENT_HEIGHTS: Record<string, number> = {
  shallow: SEA_LEVEL - 1,
  deeper: SEA_LEVEL - 2,
  deep: SEA_LEVEL - 3,
};
const gradientRows = new Map<number, string>();
function addGradient(startR: number, dir: 1 | -1): void {
  const tiers: Array<[string, number]> = [['shallow', 2], ['deeper', 2], ['deep', 2], ['shallow', 2]];
  let r = startR;
  tiers.forEach(([tier, count]) => {
    for (let i = 0; i < count; i++) { gradientRows.set(r, tier); r += dir; }
  });
}
addGradient(-3, -1);
addGradient(3, 1);
const GRADIENT_QS = [-1, 0];

const islandHexList: Axial[] = [...ISLAND_HEIGHTS.keys()].map((k) => {
  const [q, r] = k.split(',').map(Number);
  return { q, r };
});

function nearestDist(q: number, r: number, hexes: Axial[]): number {
  let best = Infinity;
  for (const h of hexes) {
    const d = axialDistance(q, r, h.q, h.r);
    if (d < best) best = d;
    if (best <= 1) break;
  }
  return best;
}

function distToIsland(q: number, r: number): number {
  return nearestDist(q, r, islandHexList);
}

// Beyond the explicit gradient above, water depth grades outward from the
// island's shore in two more tiers — deeper, then deepest — hugging the
// coast rather than an infinite noise field. Past that, it stays at the
// deepest tier forever (rather than shallowing back out), so the board's
// deep water reads as one continuous body all the way to the edges
// instead of an isolated ring around each landmass — still bounded/
// uninteresting out there (nothing procedural to find), just deep
// instead of shallow.
export function fixedHeightAt(q: number, r: number): number {
  const key = q + ',' + r;
  const islandH = ISLAND_HEIGHTS.get(key);
  if (islandH !== undefined) return islandH;
  const gradientTier = GRADIENT_QS.includes(q) ? gradientRows.get(r) : undefined;
  if (gradientTier !== undefined) return GRADIENT_HEIGHTS[gradientTier];
  const d = distToIsland(q, r);
  if (d <= 3) return SEA_LEVEL - 1; // shallow
  if (d <= 6) return SEA_LEVEL - 2; // deeper
  return SEA_LEVEL - 4; // deepest — 'deep' habitat + shipwreck depth, and everything beyond
}

// Zones each landmass's shallows to a single, exclusive habitat instead of
// the normal noise-based patches — north knoll is all coral, south knoll
// is all mangrove, and the main island (everything nearest to it) is all
// shellfish. Depth-3+ water is left alone (returns null, falling through
// to water.ts's own depth-based 'deep' rule) so the channels/wrecks still
// read as 'deep' regardless of which landmass they're nearest to.
export function fixedHabitatAt(q: number, r: number): Habitat | null {
  const h = fixedHeightAt(q, r);
  if (h >= SEA_LEVEL) return null; // dry land, not water
  if (SEA_LEVEL - h >= 3) return null; // let the normal depth-based 'deep' rule apply
  const dMain = nearestDist(q, r, mainIslandHexList);
  const dNorth = axialDistance(q, r, NORTH_KNOLL.q, NORTH_KNOLL.r);
  const dSouth = axialDistance(q, r, SOUTH_KNOLL.q, SOUTH_KNOLL.r);
  const nearest = Math.min(dMain, dNorth, dSouth);
  if (nearest === dNorth) return 'coral';
  if (nearest === dSouth) return 'mangrove';
  return 'shellfish';
}

// Forces the interior's grass texture despite sitting only one level
// above the (sand) shoreline — see the ISLAND_HEIGHTS comment above.
export function fixedTopTypeAt(q: number, r: number): number | null {
  return GRASS_HEXES.has(q + ',' + r) ? 0 : null;
}

// Guaranteed wood sources: the interior's tree-eligible hexes also go
// through chunks.ts's normal per-hex forest-noise gate (unmodified), but
// that gate alone is inconsistent — wood is essential for building, so a
// spread of interior hexes get an explicit tree instead of leaving it to
// chance. Any extra procedural hits elsewhere in the interior are a bonus.
const EXPLICIT_TREE_HEXES: Array<[number, number]> = [
  [-4, -1], [-2, -1], [0, -1], [2, -1], [4, -1],
  [-3, 0], [-1, 0], [1, 0], [3, 0],
  [-4, 1], [-2, 1], [0, 1], [2, 1], [4, 1],
  // North knoll (centered 0,-13) and south knoll (centered 0,13) interiors.
  [0, -13], [1, -13], [0, -12], [-1, -12], [-1, -13], [0, -14], [1, -14],
  [0, 13], [1, 13], [0, 14], [-1, 14], [-1, 13], [0, 12], [1, 12],
];

function villageAt(q: number, r: number): Village {
  const pos = hexToWorld(q, r);
  const demand: Record<string, number> = {}, mult: Record<string, number> = {};
  FISH_TYPES.forEach((t) => { demand[t.name] = 1; mult[t.name] = 1; });
  return { ok: true, q, r, x: pos.x, z: pos.z, demand, mult, lastT: 0 };
}

function wreckAt(q: number, r: number): WreckSite {
  const pos = hexToWorld(q, r);
  return { ok: true, q, r, x: pos.x, z: pos.z, mesh: null, loot: 4 };
}

let active = false;

export function isFixedMapActive(): boolean {
  return active;
}

const SPAWN_HEX: Axial = { q: 0, r: 0 };

// Call once from initFixedMapWorld, after applySeed(FIXED_MAP_SEED) but
// before findSpawnPoint()/loadStartArea() — those two are otherwise
// completely unmodified and just work off the overridden height function.
// findSpawnPoint() itself needs a cluster of height>=SEA_LEVEL+2 hexes
// nearby to consider a spot "good" (see entities/player.ts's
// hasNearbyTrees), which the board no longer has now that everything's a
// level lower — its search harmlessly fails and leaves player.x/z
// untouched, so setting the real spawn position here first (before that
// search runs) is what actually places the player correctly.
export function activateFixedMap(): void {
  active = true;
  setHeightOverride(fixedHeightAt);
  setTopTypeOverride(fixedTopTypeAt);
  setHabitatOverride(fixedHabitatAt);
  const spawnPos = hexToWorld(SPAWN_HEX.q, SPAWN_HEX.r);
  player.x = spawnPos.x;
  player.z = spawnPos.z;

  // Placeholders so villageFor/wreckFor (economy/market.ts, world/chunks.ts)
  // don't also procedurally site an extra village/wreck inside the board's
  // own footprint — everything beyond the board is uniformly too
  // shallow/flat for their searches to succeed anyway, so this only
  // matters for whichever region cell(s) the board itself overlaps. The
  // knolls now push the board's extent close to a single VILLAGE_REGION/
  // WRECK_REGION cell's edge, so a generous 3x3 block of region keys
  // around the origin covers it with margin to spare.
  for (let rx = -1; rx <= 1; rx++) {
    for (let rz = -1; rz <= 1; rz++) {
      const rk = rx + ',' + rz;
      villages.set(rk, { ok: false, q: 0, r: 0, x: 0, z: 0, demand: {}, mult: {}, lastT: 0 });
      wrecks.set(rk, { ok: false, q: 0, r: 0, x: 0, z: 0, mesh: null, loot: 0 });
    }
  }

  extraVillages.push(villageAt(-6, 0), villageAt(6, 0));
  // Two wrecks sit right in the north/south channels; the third is the
  // closest deep pocket to the main island's own east coast.
  wrecks.set('fixed-1', wreckAt(0, -7));
  wrecks.set('fixed-2', wreckAt(0, 7));
  wrecks.set('fixed-3', wreckAt(12, 4));

  EXPLICIT_TREE_HEXES.forEach(([q, r]) => {
    const h = ISLAND_HEIGHTS.get(q + ',' + r)!;
    spawnTree(q, r, h, hash2(q * 977 + 3, r * 613 + 11));
  });
}

export function deactivateFixedMap(): void {
  active = false;
  setHeightOverride(null);
  setTopTypeOverride(null);
  setHabitatOverride(null);
}
